// @flow

import { capitalize } from 'lodash';

import type {
  DestinationArea,
  DestinationMedia,
  DestinationPrice,
  Destinations,
  TopDestinations,
} from 'Client/cms/toolkit/destinations/destinations.resolver';

export type Destination = {
  ...$Exact<DestinationMedia>,
  label: string,
  price: {
    ...$Exact<DestinationPrice>,
    countryId?: string,
    productId?: string,
    productHref?: null | string,
  },
};

export type DestinationGroup = {
  areaId: string,
  areaLabel: string,
  destinations: Array<Destination>,
};

type PricedDestination = {
  id: string,
  label: string,
  price: {
    ...$Exact<DestinationPrice>,
    countryId?: string,
    productId?: string,
    productHref?: null | string,
  },
};

type PricedDestinations = {
  pricedAreas: Array<PricedDestination>,
  pricedCountries: Array<PricedDestination>,
};

export const TOP_DESTINATIONS_AREA_ID = 'top-destinations';

export const getPricedDestinations = (destinations: Destinations): PricedDestinations => {
  const pricedAreas = [];
  const pricedCountries = [];
  destinations?.forEach(area => {
    let areaPrice = {};

    area?.countries?.forEach(country => {
      if (country.products.length > 0) {
        let countryPrice = {};

        country.products.forEach(({ id, href, price }) => {
          const areaPriceValue = areaPrice.bestPrice?.value;
          const countryPriceValue = countryPrice.bestPrice?.value;
          const productPriceValue = price?.bestPrice?.value;

          if (!areaPriceValue || (productPriceValue && areaPriceValue > productPriceValue)) {
            areaPrice = { ...price, productId: id, productHref: href, countryId: country.id };
          }

          if (!countryPriceValue || (productPriceValue && countryPriceValue > productPriceValue)) {
            countryPrice = { ...price, productId: id, productHref: href };
          }
        });

        pricedCountries.push({
          id: country.id,
          label: capitalize(country.label),
          price: countryPrice,
        });
      }
    });

    const { label: areaLabel } = area;

    pricedAreas.push({
      id: area.id,
      label: areaLabel.charAt(0).toUpperCase() + areaLabel.slice(1),
      price: areaPrice,
    });
  });

  return { pricedAreas, pricedCountries };
};

export const getTopDestinations = (
  topDestinations: TopDestinations,
  { pricedAreas, pricedCountries }: PricedDestinations,
): DestinationGroup => {
  const areaDestinations = [].concat(
    // See https://davidwalsh.name/flow-object-values
    // eslint-disable-next-line flowtype/no-weak-types
    ...topDestinations.destinations.map((destination => Object.values(destination): any)),
  );
  return {
    areaId: TOP_DESTINATIONS_AREA_ID,
    areaLabel: topDestinations.label,
    destinations: areaDestinations.reduce((destinations, destination) => {
      const pricedDestination =
        pricedAreas.find(area => area.id === destination.id) ||
        pricedCountries.find(country => country.id === destination.id);
      if (pricedDestination) {
        destinations.push({ ...pricedDestination, ...destination });
      }
      return destinations;
    }, []),
  };
};

export const getOtherDestinations = (
  otherDestinations: Array<DestinationArea>,
  { pricedAreas, pricedCountries }: PricedDestinations,
): Array<DestinationGroup> => {
  return otherDestinations.reduce((destinations, destination) => {
    const pricedArea = pricedAreas.find(area => area.id === destination.id);
    if (pricedArea) {
      destinations.push({
        areaId: pricedArea.id,
        areaLabel: pricedArea.label,
        destinations: destination.countries.reduce((countries, country) => {
          const pricedCountry = pricedCountries.find(c => c.id === country.id);
          if (pricedCountry) {
            countries.push({ ...pricedCountry, ...country });
          }
          return countries;
        }, []),
      });
    }
    return destinations;
  }, []);
};
